<script lang="ts" setup>
  import { useToast } from '@solfacil/girassol';
  import type { Option } from '@solfacil/girassol/dist/types/components/forms/select/types';
  import { storeToRefs } from 'pinia';

  import { SEARCH_MAX_SIZE } from '~/constants';
  import {
    useGetPartnerLazyQuery,
    useGetPartnersLazyQuery,
  } from '~/types/ecommerce/generated.codegen';
  import type {
    GetPartnersQueryVariables,
    Partner,
  } from '~/types/ecommerce/generated.codegen';

  const selectPartnerToPersonifyModal = inject<Ref<boolean>>(
    'selectPartnerToPersonifyModal',
  )!;

  const userStore = useUserStore();
  const { createErrorToast } = useToast();

  const { isAdmin } = storeToRefs(userStore);

  const openedSelectPartners = ref(false);
  const filterSearchPartner = ref<GetPartnersQueryVariables>({
    term: '',
    page: 0,
    page_size: SEARCH_MAX_SIZE,
  });

  const partnersList = ref<Partner[]>([]);
  const partnerOptions = ref<Option[]>([]);

  const {
    onResult: onSearchPartnersResult,
    loading: loadingSearchPartners,
    load: fetchSearchPartners,
  } = useGetPartnersLazyQuery(filterSearchPartner, {
    fetchPolicy: 'network-only',
  });
  onSearchPartnersResult(result => {
    if (!result.data?.getPartners) {
      return;
    }

    partnersList.value = (result.data.getPartners.partners as Partner[]) || [];
    partnerOptions.value =
      result.data.getPartners.partners?.map(partner => {
        return {
          name: `${partner?.tradingName} ${partner?.cnpj}`,
          value: partner?.id,
        } as Option;
      }) || [];
  });

  const searchPartnersDebounce = useDebounceFn(fetchSearchPartners, 500);

  const fetchPartners = (partialName: string) => {
    if (partialName.length >= 3) {
      filterSearchPartner.value.term = partialName;
      searchPartnersDebounce();
    }
  };

  const selectedPartner = ref<Option>();
  const selectPartnerToPersonify = (partner: Option | Option[] | undefined) => {
    selectedPartner.value = partner as Option;
  };

  const {
    onResult: onGetPartnerResult,
    loading: loadingGetPartner,
    load: fetchGetPartner,
    onError: onErrorGetPartner,
  } = useGetPartnerLazyQuery();

  const applyPersonification = () => {
    if (selectedPartner.value) {
      fetchGetPartner(null, {
        partner_id: (selectedPartner.value as Option)?.value as number,
      });
    }
  };

  onGetPartnerResult(result => {
    const partner = result.data?.getPartner;
    if (!partner) {
      return;
    }
    userStore.setPersonifiedUser(partner as Partner);
    selectPartnerToPersonifyModal.value = false;
  });

  const closePersonificationModal = () => {
    selectPartnerToPersonifyModal.value = false;
    selectedPartner.value = undefined;
    partnerOptions.value = [];
    partnersList.value = [];
  };

  onErrorGetPartner(() => {
    createErrorToast({
      title: 'Erro ao personificar parceiro.',
      description: 'Tente novamente mais tarde.',
    });
  });

  watch(selectPartnerToPersonifyModal, value => {
    if (value) {
      setTimeout(() => {
        document
          .getElementById('select-toggle-select-partner-personification')
          ?.click();
      }, 200);
      setTimeout(() => {
        document
          .getElementById('input-select-search-select-partner-personification')
          ?.focus();
      }, 300);
    }
  });
</script>
<template>
  <SolModal
    id="personification-modal"
    :is-open="selectPartnerToPersonifyModal"
    action-secondary-text="Cancelar"
    title="Personificar parceiro"
    :size="{
      desktop: 'medium',
      mobile: 'bottom-sheet',
    }"
    @action:secondary="closePersonificationModal"
    @close="closePersonificationModal">
    <div :class="{ 'opened-search-partner': openedSelectPartners }">
      <SolSelect
        id="select-partner-personification"
        ref="selectPartner"
        :value="selectedPartner"
        name="select-partner-personalization"
        label="Busque pelo CNPJ ou nome do parceiro que deseja personificar"
        :placeholder="`${selectedPartner ? selectedPartner.name : 'Buscar'}`"
        :options="partnerOptions"
        :loading="loadingSearchPartners"
        :searchable="true"
        :fetch-on-search="true"
        @open-dropdown="openedSelectPartners = true"
        @close-dropdown="openedSelectPartners = false"
        @update:selected="selectPartnerToPersonify"
        @search="(value: string) => fetchPartners(value)">
        <template #no-data>
          <div class="no-results">
            <icon-girassol:search class="icon" />
            <span class="legend">Digite o CNPJ ou nome do parceiro</span>
          </div>
        </template>
      </SolSelect>
    </div>
    <template v-if="isAdmin" #action-primary>
      <SolButton
        id="btn-confirm-personify"
        variant="primary"
        size="medium"
        :disabled="loadingGetPartner"
        :loading="loadingGetPartner"
        @click="applyPersonification">
        Personificar
      </SolButton>
    </template>
  </SolModal>
</template>

<style lang="scss">
  .opened-search-partner {
    @apply h-[376px];
  }

  .no-results {
    @apply flex flex-col items-center justify-center gap-nano;

    .icon {
      @apply icon-size-large text-brand-primary-light text-opacity-intense;
    }

    .legend {
      @apply text-opacity-intense;
    }
  }
</style>
